import { app, HostClientType, HostName } from "@microsoft/teams-js";

const PLATFORM_DETECTION_TIMEOUT_MS = 5000;

export const Platform = {
  // We have teams.js available
  // This is HostApplication.Teams, HostApplication.Outlook and HostApplication.Office
  Microsoft: "microsoft",
  // We don't have teams.js available
  Web: "outside-microsoft",
} as const;

export type PlatformType = (typeof Platform)[keyof typeof Platform];

export const HostApplication = {
  // We are running inside Office
  Office: "office",
  // We are running inside Outlook
  Outlook: "outlook",
  // We are running inside Teams
  // See TeamsClientVariant to see whether it's a web or desktop client
  Teams: "teams",
} as const;

export type HostApplicationType =
  (typeof HostApplication)[keyof typeof HostApplication];

export const TeamsClientVariant = {
  // We are running inside a desktop client of MS Teams
  Desktop: "desktop",
  // We are running inside mobile client of MS Teams
  Mobile: "mobile",
  // We are running inside a web client of MS Teams
  Web: "web",
} as const;

export type TeamsClientVariantType =
  (typeof TeamsClientVariant)[keyof typeof TeamsClientVariant];

export type DetectPlatformResult =
  | {
      platform: typeof Platform.Web;
      hostApplication: null;
      teamsClientVariant: null;
    }
  | {
      platform: typeof Platform.Microsoft;
      hostApplication: HostApplicationType;
      teamsClientVariant: TeamsClientVariantType;
    };

function getTeamsClientVariant(
  clientType: HostClientType
): TeamsClientVariantType | null {
  switch (clientType) {
    case HostClientType.web: {
      return TeamsClientVariant.Web;
    }
    case HostClientType.desktop:
    case HostClientType.macos: {
      return TeamsClientVariant.Desktop;
    }
    case HostClientType.android:
    case HostClientType.ios:
    case HostClientType.ipados: {
      return TeamsClientVariant.Mobile;
    }
    default: {
      return null;
    }
  }
}

function getHostApplication(hostName: HostName): HostApplicationType | null {
  if ([HostName.teams, HostName.teamsModern].includes(hostName)) {
    return HostApplication.Teams;
  }

  if ([HostName.office].includes(hostName)) {
    return HostApplication.Office;
  }

  if ([HostName.outlook, HostName.outlookWin32].includes(hostName)) {
    return HostApplication.Outlook;
  }

  return null;
}

export async function detectPlatform(): Promise<DetectPlatformResult> {
  try {
    // Set a timeout for Teams initialization
    const initPromise = app.initialize();
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(
        () => reject(new Error("Teams.js initialization timeout")),
        PLATFORM_DETECTION_TIMEOUT_MS
      )
    );

    await Promise.race([initPromise, timeoutPromise]);

    // If initialization succeeds, try to get context
    const contextPromise = app.getContext();
    const contextTimeoutPromise = new Promise<undefined>((_, reject) =>
      setTimeout(
        () => reject(new Error("Teams.js context timeout")),
        PLATFORM_DETECTION_TIMEOUT_MS
      )
    );

    const context: app.Context | undefined = await Promise.race([
      contextPromise,
      contextTimeoutPromise,
    ]);

    if (!context?.app?.host?.name) {
      throw new Error("Teams.js context is not available");
    }

    const hostName = context.app.host.name;
    const { clientType } = context.app.host;

    const platform = [
      HostName.teams,
      HostName.teamsModern,
      HostName.office,
      HostName.outlook,
      HostName.outlookWin32,
    ].includes(hostName)
      ? Platform.Microsoft
      : Platform.Web;
    const hostApplication = getHostApplication(hostName);
    const teamsClientVariant =
      platform === Platform.Microsoft
        ? getTeamsClientVariant(clientType)
        : null;

    return {
      hostApplication,
      platform,
      teamsClientVariant,
    } as DetectPlatformResult;
  } catch (error) {
    if (error instanceof Error) {
      console.warn("Teams SDK detection failed:", error.message);
    }
  }

  return {
    hostApplication: null,
    platform: Platform.Web,
    teamsClientVariant: null,
  };
}
